import { createFileRoute, useSearch } from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import { useEffect, useRef } from "react";
import { motion } from "motion/react";

import { api } from "@convex/_generated/api";

import { IntegrationsContent } from "@/components/dashboard/IntegrationsContent";
import { useAllowlist } from "@/hooks/useAllowlist";
import { useIntegrations } from "@/hooks/useIntegrations";
import { useUser } from "@/hooks/useUser";

export const Route = createFileRoute("/_authenticated/integrations")({
  component: IntegrationsRoute,
  validateSearch: (search: Record<string, unknown>) => ({
    connect: (search.connect as string) || undefined,
  }),
});

function IntegrationsRoute() {
  const { connect } = useSearch({ from: "/_authenticated/integrations" });
  const autoConnectTriggered = useRef(false);
  const { user } = useUser();
  const { entries: allowlistEntries, addEntry, removeEntry } = useAllowlist();
  const makeEmailAddress = useMutation(api.emails.makeEmailAddress);
  const linqNumber = useQuery(api.linqNumbers.getMyLinqNumber, {});
  const {
    calendlyIntegration,
    connectTelegram,
    disconnectComposioIntegration,
    disconnectGoogleSheets,
    disconnectTelegram,
    facebookIntegration,
    gmailIntegration,
    googleCalendarIntegration,
    googleDocsIntegration,
    googleDriveIntegration,
    googleSheetsIntegration,
    googleSlidesIntegration,
    linkedinIntegration,
    notionIntegration,
    redditIntegration,
    shopifyIntegration,
    startComposioOAuth,
    startGoogleSheetsOAuth,
    telegramIntegration,
    telegramUsers,
  } = useIntegrations();

  // Auto-trigger OAuth when ?connect=<provider> is in the URL (deep-link from agent chat)
  useEffect(() => {
    if (!connect || autoConnectTriggered.current) return;
    autoConnectTriggered.current = true;
    if (connect === "google_sheets") {
      startGoogleSheetsOAuth();
    } else {
      startComposioOAuth({ provider: connect as "gmail" });
    }
  }, [connect, startComposioOAuth, startGoogleSheetsOAuth]);

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 md:p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <h1 className="font-display text-3xl font-semibold tracking-tight text-home-ink md:text-4xl dark:text-white">
          Integrations
        </h1>
        <p className="mt-2 mb-10 font-body text-[15px] text-home-ink/60 dark:text-white/60">
          Connect your agent to messaging channels and apps
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.05 }}
      >
        <IntegrationsContent
          email={{
            agentEmailAddress: user?.agentEmailAddress,
            allowlist: { entries: allowlistEntries, onAdd: addEntry, onRemove: removeEntry },
            onCreateEmailAddress: async () => { await makeEmailAddress({}); },
          }}
          telegramIntegration={telegramIntegration}
          telegramUsers={telegramUsers}
          onConnectTelegram={connectTelegram}
          onDisconnectTelegram={disconnectTelegram}
          googleSheetsIntegration={googleSheetsIntegration}
          onStartGoogleSheetsOAuth={startGoogleSheetsOAuth}
          onDisconnectGoogleSheets={disconnectGoogleSheets}
          gmailIntegration={gmailIntegration}
          googleDocsIntegration={googleDocsIntegration}
          googleDriveIntegration={googleDriveIntegration}
          googleSlidesIntegration={googleSlidesIntegration}
          googleCalendarIntegration={googleCalendarIntegration}
          notionIntegration={notionIntegration}
          calendlyIntegration={calendlyIntegration}
          linkedinIntegration={linkedinIntegration}
          redditIntegration={redditIntegration}
          shopifyIntegration={shopifyIntegration}
          facebookIntegration={facebookIntegration}
          onStartComposioOAuth={startComposioOAuth}
          onDisconnectComposioIntegration={disconnectComposioIntegration}
          linqNumber={linqNumber}
        />
      </motion.div>
    </div>
  );
}
